import { useState, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { SCRLR_CONFIG } from "../components/webplayerscrlr/config";

/**
 * Custom hook for drag functionality
 * @param {Object} options - Configuration options
 * @param {number} options.offset - Minimum offset from edges (default: from SCRLR_CONFIG.DRAG.OFFSET)
 * @param {number} options.topOffset - Top offset from the top of the viewport (default: from SCRLR_CONFIG.DRAG.OFFSET)
 * @param {Object} options.initialPosition - Initial position { x, y } (default: { x: 0, y: 0 })
 * @returns {Object} - { position, isDragging, handleMouseDown, dragHandlers, elementRef }
 *   - handleMouseDown: unified handler for mouse + touch (use for onMouseDown)
 *   - dragHandlers: { onMouseDown, onTouchStart } – spread onto element for touch support
 */
export default function useDrag({ offset = SCRLR_CONFIG.DRAG.OFFSET, topOffset = SCRLR_CONFIG.DRAG.OFFSET, initialPosition = { x: 0, y: 0 } } = {}) {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, elementX: 0, elementY: 0 });
  const elementRef = useRef(null);

  const getViewportSize = useCallback(() => {
    const width = window.visualViewport?.width || window.innerWidth;
    const height = window.visualViewport?.height || window.innerHeight;
    return { width, height };
  }, []);

  // Helper function to constrain position within viewport bounds
  const constrainPosition = useCallback((x, y, initialLeft, initialBottom) => {
    if (!elementRef.current) return { x, y };

    const rect = elementRef.current.getBoundingClientRect();
    const { width: viewportWidth, height: viewportHeight } = getViewportSize();

    // X constraints: element's left edge must be between offset and (viewport width - element width - offset)
    const minX = offset - initialLeft;
    const maxX = viewportWidth - rect.width - offset - initialLeft;

    // Y constraints: element's top edge must be between topOffset and (viewport height - element height - offset)
    // For bottom-positioned: top = viewportHeight - bottom - elementHeight + transformY
    const initialTop = viewportHeight - initialBottom - rect.height;
    const minY = topOffset - initialTop;
    const maxY = viewportHeight - rect.height - offset - initialTop;

    return {
      x: Math.max(minX, Math.min(x, maxX)),
      y: Math.max(minY, Math.min(y, maxY))
    };
  }, [getViewportSize, offset, topOffset]);

  const getAnchorOffsets = useCallback(() => {
    if (!elementRef.current) return { initialLeft: 0, initialBottom: 0 };
    const computedStyle = window.getComputedStyle(elementRef.current);
    return {
      initialLeft: parseFloat(computedStyle.left) || 0,
      initialBottom: parseFloat(computedStyle.bottom) || 0
    };
  }, []);

  const getClientPos = (e) => {
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    if (e.changedTouches && e.changedTouches.length > 0) {
      return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  const handleMouseDown = useCallback((e) => {
    // Don't preventDefault here so synthetic click still fires on mobile tap
    const pos = getClientPos(e);
    setIsDragging(true);
    const computedStyle = window.getComputedStyle(elementRef.current);
    const initialLeft = parseFloat(computedStyle.left) || 0;
    const initialBottom = parseFloat(computedStyle.bottom) || 0;
    
    // Disable text selection on body while dragging
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.mozUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    
    // Disable pointer events on all iframes to prevent them from capturing mouse events
    // This ensures mouseup events bubble up to document/window even when cursor is over iframe
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      iframe.style.pointerEvents = 'none';
    });
    
    dragStartRef.current = {
      mouseX: pos.x,
      mouseY: pos.y,
      elementX: position.x,
      elementY: position.y,
      initialLeft,
      initialBottom,
      iframes: Array.from(iframes) // Store iframes to restore later
    };
  }, [position]);

  /** Unified handler for both mouse and touch – attach to onMouseDown and onTouchStart */
  const handlePointerDown = useCallback((e) => {
    const pos = e.touches?.[0]
      ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
      : { x: e.clientX, y: e.clientY };
    handleMouseDown({ ...e, clientX: pos.x, clientY: pos.y });
  }, [handleMouseDown]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !elementRef.current) return;
    e.preventDefault(); // Prevent text selection and default drag during actual drag
    const pos = getClientPos(e);
    const deltaX = pos.x - dragStartRef.current.mouseX;
    const deltaY = pos.y - dragStartRef.current.mouseY;
    
    // Calculate new position - allow free movement in both directions
    const newX = dragStartRef.current.elementX + deltaX;
    const newY = dragStartRef.current.elementY + deltaY;
    
    // Calculate constraints accounting for initial CSS positioning (bottom/left)
    const { initialLeft, initialBottom } = dragStartRef.current;
    
    const constrainedPosition = constrainPosition(newX, newY, initialLeft, initialBottom);
    setPosition(constrainedPosition);
  }, [isDragging, constrainPosition]);

  const handleMouseUp = useCallback((e) => {
    setIsDragging(false);
    
    // Re-enable pointer events on iframes that were disabled during drag
    if (dragStartRef.current && dragStartRef.current.iframes) {
      dragStartRef.current.iframes.forEach(iframe => {
        iframe.style.pointerEvents = '';
      });
    }
    
    // Re-enable text selection on body
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.mozUserSelect = '';
    document.body.style.msUserSelect = '';
  }, []);

  // Constrain initial position when element mounts (synchronously before paint)
  useLayoutEffect(() => {
    if (!elementRef.current) return;
    const { initialLeft, initialBottom } = getAnchorOffsets();
    const constrainedPosition = constrainPosition(initialPosition.x, initialPosition.y, initialLeft, initialBottom);

    if (constrainedPosition.x !== initialPosition.x || constrainedPosition.y !== initialPosition.y) {
      setPosition(constrainedPosition);
    }
  }, [constrainPosition, getAnchorOffsets, initialPosition.x, initialPosition.y]);

  // Re-apply constraints when viewport changes (tablet rotate, browser UI collapse/expand).
  useEffect(() => {
    const reConstrainCurrentPosition = () => {
      if (!elementRef.current) return;
      const { initialLeft, initialBottom } = getAnchorOffsets();
      setPosition((prev) => constrainPosition(prev.x, prev.y, initialLeft, initialBottom));
    };

    const viewport = window.visualViewport;
    window.addEventListener('resize', reConstrainCurrentPosition);
    window.addEventListener('orientationchange', reConstrainCurrentPosition);
    viewport?.addEventListener('resize', reConstrainCurrentPosition);

    return () => {
      window.removeEventListener('resize', reConstrainCurrentPosition);
      window.removeEventListener('orientationchange', reConstrainCurrentPosition);
      viewport?.removeEventListener('resize', reConstrainCurrentPosition);
    };
  }, [constrainPosition, getAnchorOffsets]);

  useEffect(() => {
    if (!isDragging) return;
    
    const handleTouchMove = (e) => {
      handleMouseMove(e);
    };
    const handleTouchEnd = (e) => {
      handleMouseUp(e);
    };
    
    // Listen on both document and window for better iframe compatibility
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseup', handleMouseUp);
    // Also use pointer events which work better with iframes
    document.addEventListener('pointermove', handleMouseMove);
    document.addEventListener('pointerup', handleMouseUp);
    window.addEventListener('pointerup', handleMouseUp);
    // Touch events for tablets (some don't synthesize pointer events during touch drag)
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('pointermove', handleMouseMove);
      document.removeEventListener('pointerup', handleMouseUp);
      window.removeEventListener('pointerup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchend', handleTouchEnd);
      
      // Cleanup: restore iframe pointer events and text selection if component unmounts or drag stops unexpectedly
      if (dragStartRef.current && dragStartRef.current.iframes) {
        dragStartRef.current.iframes.forEach(iframe => {
          iframe.style.pointerEvents = '';
        });
      }
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.mozUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return {
    position,
    isDragging,
    elementRef,
    handleMouseDown: handlePointerDown,
    dragHandlers: { onMouseDown: handlePointerDown, onTouchStart: handlePointerDown }
  };
}

